<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import Dialog from 'primevue/dialog';
import ToggleSwitch from 'primevue/toggleswitch';
import Button from 'primevue/button';
import { useGdprConsent } from './useGdprConsent';

const {
  performanceCookies,
  functionalCookies,
  marketingCookies,
  hasConsented,
  isSubmitting,
  hasSubmissionError,
  isInitializing,
  registerConsentDialog,
  init,
  acceptAll,
  rejectAll,
  saveCustom,
} = useGdprConsent();

const unregisterConsentDialog = registerConsentDialog();
onUnmounted(unregisterConsentDialog);

type View = 'intro' | 'customize';
const currentView = ref<View>('intro');
const activeTab = ref(0);

const DIALOG_PT = {
  header: { class: '!hidden' },
  content: { class: '!p-0' },
  footer: { class: 'border-t border-outline-variant !p-4 bg-surface-low' },
} as const;

const tabs = [
  { value: 0, label: 'Your Privacy' },
  { value: 1, label: 'Strictly Necessary Cookies' },
  { value: 2, label: 'Performance Cookies' },
  { value: 3, label: 'Functional Cookies' },
  { value: 4, label: 'Marketing Cookies' },
  { value: 5, label: 'Cookie Policy' },
];

function showCustomize(tab = 0) {
  activeTab.value = tab;
  currentView.value = 'customize';
}

onMounted(() => {
  init();
});
</script>

<template>
  <Dialog
    :visible="!isInitializing && !hasConsented"
    modal
    :closable="false"
    :close-on-escape="false"
    :baseZIndex="2000"
    :pt="DIALOG_PT"
    class="gdpr-modal"
  >
    <div class="ratio-design-provider">
      <!-- INTRO VIEW -->
      <div v-if="currentView === 'intro'" class="p-6">
        <p class="text-lg font-medium mb-3">Privacy Preference Center</p>
        <p class="text-sm text-on-surface-variant leading-relaxed">
          We use cookies to help our website operate securely and reliably, improve system efficiency, and support
          marketing activities.
          <br />
          <span>
            For further information, please refer to our
            <button class="underline" @click="showCustomize(5)">Cookie Policy</button>
          </span>
        </p>
      </div>

      <!-- CUSTOMIZE VIEW -->
      <div v-else class="flex flex-col lg:flex-row">
        <!-- Left sidebar: tab navigation -->
        <div class="lg:w-1/3 border-b lg:border-b-0 lg:border-r border-outline-variant p-4">
          <p class="font-medium mb-4">Customize Privacy Settings</p>
          <nav class="flex flex-col gap-1">
            <button
              v-for="tab in tabs"
              :key="tab.value"
              class="text-left px-3 py-2 rounded text-sm transition-colors"
              :class="
                activeTab === tab.value
                  ? 'bg-surface-container text-on-surface font-medium'
                  : 'text-on-surface-variant hover:bg-surface-container-low'
              "
              @click="activeTab = tab.value"
            >
              {{ tab.label }}
            </button>
          </nav>
        </div>

        <!-- Right content: tab panels -->
        <div class="lg:w-2/3 p-6 overflow-y-auto max-h-[60vh]">
          <!-- Tab 0: Your Privacy -->
          <div v-show="activeTab === 0">
            <p class="font-semibold mb-3">Your Privacy</p>
            <p class="text-sm text-on-surface-variant leading-relaxed">
              When you visit any website it may use cookies and web beacons to store or retrieve information on your
              browser. This information might be about you, your preferences or your device and is typically used to
              make the website work as expected. The information does not usually directly identify you, but can provide
              a personalized browsing experience. Because we respect your right to privacy, you can choose not to allow
              some types of cookies and web beacons. Please click on the different category headings to find out more
              and change our default settings. However, blocking some types of cookies may impact your experience on our
              website and limit the services we can offer.
            </p>
          </div>

          <!-- Tab 1: Strictly Necessary Cookies -->
          <div v-show="activeTab === 1">
            <p class="font-semibold mb-3">Strictly Necessary Cookies</p>
            <p class="text-sm text-on-surface-variant leading-relaxed">
              Strictly necessary cookies are necessary for the website to function and cannot be switched off in our
              systems. They are typically set in response to actions made by you which amount to a request for services,
              such as setting your privacy preferences, logging in or filling in forms. While you can set your browser
              to block or alert you about these cookies, some parts of the website will not work. These cookies do not
              store any personally identifiable information.
            </p>
            <p class="text-success text-sm mt-4">Always Active</p>
          </div>

          <!-- Tab 2: Performance Cookies -->
          <div v-show="activeTab === 2">
            <p class="font-semibold mb-3">Performance Cookies and Web Beacons</p>
            <p class="text-sm text-on-surface-variant leading-relaxed">
              Performance cookies and web beacons allow us to count visits and traffic sources so we can measure and
              improve website performance. They help us to know which pages are the most and least popular and see how
              visitors navigate around our website. All information these cookies and web beacons collect is aggregated
              and anonymous. If you do not allow these cookies and web beacons we will not know when you have visited
              our website and will not be able to monitor its performance.
            </p>
            <div class="flex items-center gap-3 mt-4">
              <ToggleSwitch v-model="performanceCookies" input-id="toggle-performance" />
              <label
                for="toggle-performance"
                class="text-sm"
                :class="performanceCookies ? 'text-success' : 'text-on-surface-variant'"
              >
                {{ performanceCookies ? 'Active' : 'Inactive' }}
              </label>
            </div>
          </div>

          <!-- Tab 3: Functional Cookies -->
          <div v-show="activeTab === 3">
            <p class="font-semibold mb-3">Functional Cookies</p>
            <p class="text-sm text-on-surface-variant leading-relaxed">
              Functional cookies enable our website to provide enhanced functionality and personalisation. They may be
              set by us or by third party providers whose services we have added to our pages. If you do not allow these
              cookies then some or all of these services may not function properly.
            </p>
            <div class="flex items-center gap-3 mt-4">
              <ToggleSwitch v-model="functionalCookies" input-id="toggle-functional" />
              <label
                for="toggle-functional"
                class="text-sm"
                :class="functionalCookies ? 'text-success' : 'text-on-surface-variant'"
              >
                {{ functionalCookies ? 'Active' : 'Inactive' }}
              </label>
            </div>
          </div>

          <!-- Tab 4: Marketing Cookies -->
          <div v-show="activeTab === 4">
            <p class="font-semibold mb-3">Marketing Cookies and Web Beacons</p>
            <p class="text-sm text-on-surface-variant leading-relaxed">
              Marketing Cookies and web beacons may be set through our website by our advertising partners. They may be
              used by those companies to build a profile of your interests and show you relevant adverts on other
              websites. They do not directly store personal information, but uniquely identify your browser and internet
              device. If you do not allow these cookies and web beacons, you will experience less targeted advertising.
              Our website does not track users when they cross to third party websites, does not provide targeted
              advertising to them and therefore does not respond to "Do Not Track" signals.
            </p>
            <div class="flex items-center gap-3 mt-4">
              <ToggleSwitch v-model="marketingCookies" input-id="toggle-marketing" />
              <label
                for="toggle-marketing"
                class="text-sm"
                :class="marketingCookies ? 'text-success' : 'text-on-surface-variant'"
              >
                {{ marketingCookies ? 'Active' : 'Inactive' }}
              </label>
            </div>
          </div>

          <!-- Tab 5: Cookie Policy -->
          <div v-show="activeTab === 5" class="text-sm text-on-surface-variant leading-relaxed">
            <h2 class="text-base font-semibold text-on-surface mb-4">Interactive Brokers Group Cookie Policy</h2>

            <p class="font-semibold text-on-surface mt-4 mb-2">What are Cookies and Web Beacons?</p>
            <p>
              Cookies are pieces of data that a website transfers to a user's hard drive for record-keeping purposes.
              Web beacons are transparent pixel images that are used in collecting information about website usage,
              e-mail response and tracking. Generally, cookies may contain information about your Internet Protocol
              ("IP") addresses, the region or general location where your computer or device is accessing the internet,
              browser type, operating system and other usage information about the website or your usage of our
              services, including a history of the pages you view.
            </p>

            <p class="font-semibold text-on-surface mt-4 mb-2">How We Use Cookies and Web Beacons</p>
            <p>
              Interactive Brokers Group collects information from cookies and web beacons and stores it in an internal
              database. This information is retained in accordance with our Privacy Policy. This website uses the
              following cookies and web beacons:
            </p>

            <p class="font-semibold text-on-surface mt-4 mb-2">Strictly Necessary Cookies</p>
            <p>
              These cookies are necessary for the website to function and cannot be switched off in our systems. They
              are usually only set in response to actions made by you which amount to a request for services, such as
              setting your privacy preferences, logging in or filling in forms. You can configure your browser to block
              or alert you about these cookies, but certain areas of the site will not function properly. These cookies
              do not store any personal data.
            </p>

            <p class="font-semibold text-on-surface mt-4 mb-2">Performance Cookies and Web Beacons</p>
            <p>
              These cookies and web beacons allow us to count visits and traffic sources so we can measure and improve
              the performance of our site. They help us to know which pages are the most and least popular and see how
              visitors move around the site. All information that these cookies and web beacons collect is aggregated
              and, therefore, anonymous. If you do not allow these cookies and web beacons our aggregated statistics
              will not have a record of your visit.
            </p>
            <p class="mt-2">
              The website uses Google Analytics, a web analytics service provided by Google, Inc. ("Google"). Google
              Analytics uses cookies to help analyse how you use this website. The information generated by the cookie
              about your use of this website (including your IP address) will be transmitted to and stored by Google on
              servers in the United States. Google will use this information for the purposes of evaluating your use of
              the website, compiling reports on website activity for website operators and providing other services
              relating to website activity and internet usage. Google may also transfer this information to third
              parties where required to do so by law, or where such third parties process the information on Google's
              behalf. Google will not associate your IP address with any other data held by Google.
            </p>

            <p class="font-semibold text-on-surface mt-4 mb-2">Functional Cookies</p>
            <p>
              These cookies enable the website to provide enhanced functionality and personalization. They may be set by
              us or by third party providers whose services we have added to our pages. If you do not allow these
              cookies, some or all of these services may not function properly.
            </p>

            <p class="font-semibold text-on-surface mt-4 mb-2">Marketing Cookies and Web Beacons</p>
            <p>
              These cookies and web beacons may be set throughout our site by our advertising partners. They may be used
              by those companies to build a profile of your interests and show you relevant advertisements on other
              sites. They do not store personal information that could identify you directly, but are based on uniquely
              identifying your browser and internet device. If you do not allow these cookies and web beacons, you will
              experience less targeted advertising. The website does not track users when they cross to third party
              websites, does not provide targeted advertising to them and therefore does not respond to Do Not Track
              ("DNT") signals.
            </p>

            <p class="font-semibold text-on-surface mt-4 mb-2">Managing Your Cookie Preferences</p>
            <p>
              You have many choices with regards to the management of cookies on your computer. All major browsers allow
              you to block or delete cookies from your system. However, if you do decide to disable cookies you may not
              be able to access some areas of our website or the website may function incorrectly. To learn more about
              your ability to manage cookies and web beacons and how to disable them, please consult the privacy
              features in your browser or visit
              <a
                href="https://www.allaboutcookies.org/"
                target="_blank"
                rel="noopener noreferrer"
                class="underline text-primary"
              >
                www.allaboutcookies.org </a
              >.
            </p>
            <p class="mt-2">
              This website may link through to third party websites which may also use cookies and web beacons over
              which we have no control. We recommend that you check the relevant third parties privacy policy for
              information about any cookies and web beacons that may be used.
            </p>
          </div>
        </div>
      </div>
      <div v-if="hasSubmissionError" role="alert" class="p-4">
        <ib-error-state message="We could not save your cookie preferences. Please try again." label="" />
      </div>
    </div>

    <template #footer>
      <!-- Intro view footer -->
      <div v-if="currentView === 'intro'" class="flex gap-2 justify-end">
        <Button label="Customize Settings" severity="secondary" :disabled="isSubmitting" @click="showCustomize(0)" />
        <Button label="Reject All Cookies" severity="secondary" :disabled="isSubmitting" @click="rejectAll()" />
        <Button label="Accept Cookies" severity="primary" :disabled="isSubmitting" @click="acceptAll()" />
      </div>
      <!-- Customize view footer -->
      <div v-else class="flex gap-2 justify-end">
        <Button label="Save Custom Settings" severity="secondary" :disabled="isSubmitting" @click="saveCustom()" />
        <Button label="Accept Cookies" severity="primary" :disabled="isSubmitting" @click="acceptAll()" />
      </div>
    </template>
  </Dialog>
</template>

<style>
/* Global style needed since Dialog portals to body */
.gdpr-modal.p-dialog {
  width: 90vw !important;
  max-width: 64rem !important;
}
</style>
<!-- last modified by AI tools on 2026-10-16 -->
